// Eventos longos (festivais, exposições, temporadas) — espelho da regra do
// app Flutter. Recebe eventos já normalizados pela área pública
// ({ _startDate, _endDate, boosted }). Só filtro/ordenação no front, sem
// campo novo no Firestore.
//
// Regra:
// - longo = duração (startDate → endDate) maior que 4 dias;
// - na listagem principal sem filtro: aparece antes de começar, no dia da
//   abertura e nos 2 últimos dias; some no meio do período;
// - sempre aparece com filtro (data/categoria/busca) e quando impulsionado;
// - com filtro, vai pro fim da lista (impulsionado não vai);
// - seção "Em cartaz": todos os longos em andamento, sem limite.

const DAY_MS = 24 * 60 * 60 * 1000;
const LONG_EVENT_MIN_DURATION_MS = 4 * DAY_MS;

// Dia de calendário sempre em Brasília, via Intl (sem offset manual —
// quem cuida do fuso, inclusive de eventual horário de verão, é o próprio
// navegador). en-CA formata como "2026-10-18".
const BRASILIA_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Número sequencial do dia em Brasília — permite "último dia - 1" com
// aritmética simples. Date.UTC aqui só converte ano/mês/dia em número; não
// é conversão de fuso.
function brasiliaDayNumber(date) {
  const [y, m, d] = BRASILIA_DAY_FORMATTER.format(date).split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function isLongEvent(event) {
  const start = event?._startDate;
  const end = event?._endDate;
  if (!start || !end) return false;
  return end.getTime() - start.getTime() > LONG_EVENT_MIN_DURATION_MS;
}

// Longo que já começou e ainda não terminou.
export function isLongEventOngoing(event, now = new Date()) {
  if (!isLongEvent(event)) return false;
  const t = now.getTime();
  return event._startDate.getTime() <= t && t <= event._endDate.getTime();
}

// Visível na listagem principal (sem filtro)? Evento curto, impulsionado
// ou que ainda não começou: sempre. Longo em andamento: só no dia da
// abertura e nos 2 últimos dias.
export function isVisibleInMainList(event, now = new Date()) {
  if (event?.boosted || !isLongEvent(event)) return true;
  if (now.getTime() < event._startDate.getTime()) return true;

  const today = brasiliaDayNumber(now);
  const firstDay = brasiliaDayNumber(event._startDate);
  const lastDay = brasiliaDayNumber(event._endDate);
  return today === firstDay || today >= lastDay - 1;
}

// Longo já no 3º dia ou depois (hoje, em Brasília, é pelo menos 2 dias
// depois do dia da abertura). Antes de começar e nos 2 primeiros dias: não.
// Usado na ordenação padrão da aba "Aprovados" do painel admin.
export function isLongEventFromThirdDay(event, now = new Date()) {
  if (!isLongEvent(event)) return false;
  return brasiliaDayNumber(now) - brasiliaDayNumber(event._startDate) >= 2;
}

// Listagem principal sem filtro: tira os longos do meio do período.
export function filterMainList(events, now = new Date()) {
  return events.filter((e) => isVisibleInMainList(e, now));
}

// Listagem com filtro (data, categoria, busca): todos aparecem, mas os
// longos não impulsionados vão pro fim, mantendo a ordem relativa.
export function sortLongEventsLast(events) {
  const regular = [];
  const long = [];
  for (const e of events) {
    (isLongEvent(e) && !e.boosted ? long : regular).push(e);
  }
  return [...regular, ...long];
}

// Seção "Em cartaz": todos os longos em andamento, os que acabam antes
// primeiro.
export function getOngoingLongEvents(events, now = new Date()) {
  return events
    .filter((e) => isLongEventOngoing(e, now))
    .sort((a, b) => a._endDate.getTime() - b._endDate.getTime());
}
